import type { useNutritionRecovery } from "../../services/use-nutrition-recovery"
import { humanizeEnumLabel } from "../../lib/enum-label"
import { formatDateTime } from "../../lib/format-date"
import { Button } from "../ui/button"
import { StatusBadge } from "../ui/status-badge"

type Recovery = ReturnType<typeof useNutritionRecovery>
export function NutritionRecoveryResult({
  recovery,
  index,
  paused,
}: {
  recovery: Recovery
  index: number
  paused: boolean
}) {
  const id = recovery.recoveryIds[index]
  const query = recovery.recoveries[index]
  const active = recovery.verification.sessions.find((item) => item.id === id)
  if (!id || !query) {
    return null
  }
  return (
    <div className="space-y-2 text-xs">
      {query.error && (
        <p
          role="alert"
          className="text-destructive"
        >{`Recovery read failed: ${query.error.message}. The work may still be running.`}</p>
      )}

      {query.isPending && <p>Reading admitted recovery…</p>}

      {active?.error && (
        <p
          role="alert"
          className="text-destructive"
        >{`Verification stopped: ${active.error}. Preview again before continuing.`}</p>
      )}

      <RecoveryVerificationControls
        recovery={recovery}
        id={id}
        paused={paused}
        resolved={query.data?.state === "resolved"}
      />

      {query.data && (
        <>
          <StatusBadge
            tone={query.data.state === "resolved" ? "success" : "warning"}
          >{`Recovery ${humanizeEnumLabel(query.data.state)}`}</StatusBadge>

          <p className="break-all font-mono">{query.data.id}</p>

          <p>{query.data.blockers.map(humanizeEnumLabel).join(" · ")}</p>

          <p>{`Fill jobs: ${
            query.data.linkedWork.fillJobIds?.join(", ") ??
            query.data.linkedWork.fillJobId ??
            "None"
          } · Replacement jobs:${query.data.linkedWork.replacementIds.length}`}</p>

          {query.data.resolution && (
            <p>{`Resolved ${formatDateTime(query.data.resolution.resolvedAt)}. Proof covers ${query.data.resolution.proof.footprint.length} scopes.`}</p>
          )}
        </>
      )}
    </div>
  )
}
function RecoveryVerificationControls({
  recovery,
  id,
  paused,
  resolved,
}: {
  recovery: Recovery
  id: string
  paused: boolean
  resolved: boolean
}) {
  const { preview, verification } = recovery
  const active = verification.sessions.find((item) => item.id === id)
  const canContinue =
    preview.data?.verificationRevision === verification.previewRevision &&
    preview.data?.targets.some(
      (target) =>
        target.recoveryId === id &&
        target.classification !== "unavailable" &&
        target.affectedScopes.length > 0 &&
        !target.resolved
    )
  if (resolved) {
    return null
  }
  return (
    <>
      {active?.active && (
        <Button
          className="min-h-11"
          disabled={paused || verification.isPending}
          onClick={() => verification.advance(active)}
        >
          Verify recovery progress
        </Button>
      )}

      {!active?.active && (
        <>
          <p>Verification is stopped. Preview affected scopes before continuing.</p>

          {canContinue && (
            <Button
              className="min-h-11"
              disabled={paused || verification.isPending || preview.isPending}
              onClick={() => {
                if (preview.data) {
                  verification.continueVerification(id, preview.data.verificationRevision)
                }
                preview.reset()
              }}
            >
              Continue recovery verification
            </Button>
          )}
        </>
      )}
    </>
  )
}
